import { push } from 'connected-react-router'
import queryString from 'query-string'
import { isEmpty } from 'lodash'

import {
  createAccountApi,
  getUserPotentialTeamsApi,
  selfJoinTeamApi,
  updateAccountApi,
} from '../../services/team'
import { selectCreatedTeam } from '../selectors/teamOnboardingSelectors'
import { selectProfile } from '../modules/selectors'
import { createAction, createAsyncAction, FULFILLED, PENDING, REJECTED } from '../utils'
import { publishClickedNext } from '../../util/events/teamsOnboardingEventPublishers'
import { VIEWS } from '../../util/events/teamsEvents'
import { inviteMembersStart } from './teamMembers'
import { enqueueSnackbar } from './snackbar'
import { loadUserTeamStart } from './userTeam'

export const STEPS = Object.freeze({
  CREATE_OR_JOIN: 0,
  TEAM_NAME: 1,
  TEAM_DOMAIN: 2,
  TEAM_MEMBERS: 3,
  COMPLETE: 7,
})

// ------------------------------------
// Action Types & Creators
// ------------------------------------
export const STATE_RESET_TEAM_ONBOARDING = 'foodsby/teamOnboarding/STATE_RESET_TEAM_ONBOARDING'
export const NEXT_STEP_TEAM_NAME = 'foodsby/teamOnboarding/NEXT_STEP_TEAM_NAME'
export const NEXT_STEP_TEAM_DOMAIN = 'foodsby/teamOnboarding/NEXT_STEP_TEAM_DOMAIN'
export const NEXT_STEP_TEAM_MEMBERS = 'foodsby/teamOnboarding/NEXT_STEP_TEAM_MEMBERS'
export const NEXT_STEP_COMPLETE = 'foodsby/teamOnboarding/NEXT_STEP_COMPLETE'
export const POTENTIAL_TEAMS_SET = 'foodsby/teamOnboarding/POTENTIAL_TEAMS_SET'
export const TEAM_JOIN = 'foodsby/teamOnboarding/TEAM_JOIN'

export const resetTeamOnboardingState = createAction(STATE_RESET_TEAM_ONBOARDING)
export const setNextStepTeamName = createAction(NEXT_STEP_TEAM_NAME)
export const setNextStepTeamDomain = createAsyncAction(NEXT_STEP_TEAM_DOMAIN)
export const setNextStepTeamMembers = createAsyncAction(NEXT_STEP_TEAM_MEMBERS)
export const setPotentialTeams = createAsyncAction(POTENTIAL_TEAMS_SET)
export const joinTeam = createAsyncAction(TEAM_JOIN)
export const setNextStepCompletePending = createAction(PENDING(NEXT_STEP_COMPLETE))
export const setNextStepCompleteFulfilled = createAction(FULFILLED(NEXT_STEP_COMPLETE))
export const setNextStepCompleteRejected = createAction(REJECTED(NEXT_STEP_COMPLETE))

// ------------------------------------
// Thunks
// ------------------------------------
export const submitTeamNameStart = name => {
  return async dispatch => {
    try {
      await dispatch(setNextStepTeamDomain(createAccountApi(name)))
      publishClickedNext(VIEWS.STEP_NAME)
    } catch (ex) {
      publishClickedNext(VIEWS.STEP_NAME, ex?.message)
    }
  }
}

export const submitTeamDomainStart = (emailDomain, allowSelfJoin) => {
  return async (dispatch, getState) => {
    const state = getState()
    const createdTeam = selectCreatedTeam(state)
    if (createdTeam?.id) {
      try {
        await dispatch(
          setNextStepTeamMembers(updateAccountApi(createdTeam.id, emailDomain, allowSelfJoin)),
        )
        publishClickedNext(VIEWS.STEP_DOMAIN)
      } catch (ex) {
        publishClickedNext(VIEWS.STEP_DOMAIN, ex?.message)
      }
    }
  }
}

export const submitTeamMembersStart = memberEmails => {
  return async (dispatch, getState) => {
    const state = getState()
    const createdTeam = selectCreatedTeam(state)
    const profile = selectProfile(state)

    if (createdTeam?.id && profile?.locationId) {
      dispatch(setNextStepCompletePending())
      try {
        if (!isEmpty(memberEmails)) {
          await dispatch(inviteMembersStart(memberEmails, createdTeam))
        }
        publishClickedNext(VIEWS.STEP_INVITE, undefined, memberEmails?.length)
        return dispatch(setNextStepCompleteFulfilled())
      } catch (ex) {
        publishClickedNext(VIEWS.STEP_INVITE, ex?.message)
        return dispatch(setNextStepCompleteRejected(ex))
      }
    }
  }
}

export const loadPotentialTeamsStart = () => {
  return async dispatch => {
    try {
      await dispatch(setPotentialTeams(getUserPotentialTeamsApi()))
    } catch (ex) {
      dispatch(
        enqueueSnackbar({
          message:
            'Something went wrong loading the teams to join. Please try again later or contact support.',
        }),
      )
    }
  }
}

export const joinTeamStart = (accountId, name) => {
  return async dispatch => {
    try {
      await dispatch(joinTeam(selfJoinTeamApi(accountId)))
      dispatch(enqueueSnackbar({ message: `Congrats! You've just joined team ${name}.` }))
      dispatch(push({ search: queryString.stringify({ createTeam: undefined }) }))
    } catch (ex) {
      dispatch(
        enqueueSnackbar({
          message: 'Something went wrong while joining. Please try again later or contact support.',
        }),
      )
      return
    }

    dispatch(loadUserTeamStart())
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [STATE_RESET_TEAM_ONBOARDING]: () => {
    return {
      ...initialState,
      currentStep: undefined,
    }
  },
  [NEXT_STEP_TEAM_NAME]: state => {
    return {
      ...state,
      currentStep: STEPS.TEAM_NAME,
    }
  },
  [PENDING(NEXT_STEP_TEAM_DOMAIN)]: state => {
    return {
      ...state,
      isSettingNextStep: true,
    }
  },
  [FULFILLED(NEXT_STEP_TEAM_DOMAIN)]: (state, action) => {
    return {
      ...state,
      isSettingNextStep: false,
      successSettingNextStep: true,
      errorSettingNextStep: undefined,
      currentStep: STEPS.TEAM_DOMAIN,
      createdTeam: action.payload,
    }
  },
  [REJECTED(NEXT_STEP_TEAM_DOMAIN)]: (state, action) => {
    return {
      ...state,
      isSettingNextStep: false,
      errorSettingNextStep: action.payload?.data,
      successSettingNextStep: false,
    }
  },
  [PENDING(NEXT_STEP_TEAM_MEMBERS)]: state => {
    return {
      ...state,
      isSettingNextStep: true,
    }
  },
  [FULFILLED(NEXT_STEP_TEAM_MEMBERS)]: state => {
    return {
      ...state,
      isSettingNextStep: false,
      successSettingNextStep: true,
      errorSettingNextStep: undefined,
      currentStep: STEPS.TEAM_MEMBERS,
    }
  },
  [REJECTED(NEXT_STEP_TEAM_MEMBERS)]: (state, action) => {
    return {
      ...state,
      isSettingNextStep: false,
      errorSettingNextStep: action.error,
      successSettingNextStep: false,
    }
  },
  [PENDING(NEXT_STEP_COMPLETE)]: state => {
    return {
      ...state,
      isSettingNextStep: true,
      successSettingNextStep: false,
      errorSettingNextStep: undefined,
    }
  },
  [FULFILLED(NEXT_STEP_COMPLETE)]: state => {
    return {
      ...state,
      isSettingNextStep: false,
      successSettingNextStep: true,
      errorSettingNextStep: undefined,
      currentStep: STEPS.COMPLETE,
    }
  },
  [REJECTED(NEXT_STEP_COMPLETE)]: (state, action) => {
    return {
      ...state,
      isSettingNextStep: false,
      errorSettingNextStep: action.error,
    }
  },
  [PENDING(POTENTIAL_TEAMS_SET)]: state => {
    return {
      ...state,
      isLoadingPotentialTeams: true,
    }
  },
  [FULFILLED(POTENTIAL_TEAMS_SET)]: (state, action) => {
    return {
      ...state,
      potentialTeams: action.payload.content,
      isLoadingPotentialTeams: false,
    }
  },
  [REJECTED(POTENTIAL_TEAMS_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingPotentialTeams: false,
      errorLoadingPotentialTeams: action.payload.message,
    }
  },
  [PENDING(TEAM_JOIN)]: state => {
    return {
      ...state,
      isJoiningTeam: true,
    }
  },
  [FULFILLED(TEAM_JOIN)]: state => {
    return {
      ...state,
      isJoiningTeam: false,
    }
  },
  [REJECTED(TEAM_JOIN)]: (state, action) => {
    return {
      ...state,
      isJoiningTeam: false,
      errorJoiningTeam: action.payload.message,
    }
  },
}

export const initialState = {
  currentStep: STEPS.CREATE_OR_JOIN,
  createdTeam: undefined,
  potentialTeams: undefined,
  // Loading states
  isSettingNextStep: false,
  isLoadingPotentialTeams: false,
  isJoiningTeam: false,
  // Success states
  successSettingNextStep: false,
  // Error states
  errorSettingNextStep: undefined,
  errorLoadingPotentialTeams: undefined,
  errorJoiningTeam: undefined,
}

export default function teamOnboarding(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}
